import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {Car, ChevronRight, ArrowRight, AlertTriangle, Calendar, Clock} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { computeVehicleAvailability } from '@/lib/availability'

// Définition des catégories avec icône et gradient
const CATEGORIES_CONFIG: Record<string, { icon: string; gradient: string }> = {
  Fourgon: { icon: '🚐', gradient: 'from-amber-500 to-orange-600' },
  Plateau: { icon: '🛻', gradient: 'from-slate-600 to-slate-800' },
  Citadine: { icon: '🚗', gradient: 'from-emerald-500 to-emerald-700' },
  SUV: { icon: '🏎️', gradient: 'from-red-500 to-red-700' },
  Berline: { icon: '🚘', gradient: 'from-cyan-500 to-cyan-700' },
  Premium: { icon: '✨', gradient: 'from-yellow-500 to-amber-600' },
  Monospace: { icon: '🚙', gradient: 'from-teal-500 to-teal-700' },
}

function formatDateFr(dateStr: string): string {
  const date = new Date(dateStr)
  const day = date.getDate()
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
  const month = months[date.getMonth()]
  return `${day} ${month}`
}

interface CategoryWithVehicle {
  category: string
  vehicle: any
  availableNow: boolean
  nextAvailableDate?: string
  bookedUntil?: string
  vehicleCount: number
}

export default function AvailableVehicles() {
  const [categoryData, setCategoryData] = useState<CategoryWithVehicle[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const now = new Date().toISOString()

    Promise.all([
      lumi.entities.vehicles.list({
        filter: { status: { $in: ['available', 'disponible'] } },
        limit: 100,
        sort: { pricePerDay: 1 },
      }),
      lumi.entities.vehicle_bookings.list({
        filter: {
          status: { $in: ['pending', 'confirmed', 'paid', 'active'] },
          endDate: { $gte: now },
        },
        sort: { endDate: 1 },
        limit: 200,
      }),
    ])
      .then(([vehiclesRes, reservationsRes]) => {
        const allVehicles = vehiclesRes?.list || []
        const activeReservations = reservationsRes?.list || []

        // Construire la map des réservations par véhicule
        const vehicleReservationMap = new Map<string, { startDate: string; endDate: string }[]>()
        activeReservations.forEach((r: any) => {
          const existing = vehicleReservationMap.get(r.vehicleId) || []
          existing.push({ startDate: r.startDate, endDate: r.endDate })
          vehicleReservationMap.set(r.vehicleId, existing)
        })

        const nowDate = new Date()

        // Regrouper par catégorie et trouver le véhicule disponible le plus tôt
        const categoryMap = new Map<string, { vehicles: any[] }>()
        allVehicles.forEach((v: any) => {
          const cat = v.category || 'Autre'
          const entry = categoryMap.get(cat) || { vehicles: [] }
          entry.vehicles.push(v)
          categoryMap.set(cat, entry)
        })

        const results: CategoryWithVehicle[] = []

        categoryMap.forEach((data, category) => {
          let bestVehicle: any = null
          let bestAvailableNow = false
          let bestNextDate: string | undefined
          let bestBookedUntil: string | undefined = undefined

          data.vehicles.forEach((v: any) => {
            const reservations = vehicleReservationMap.get(v._id) || []
            const { availableNow, nextAvailableDate, bookedUntil } = computeVehicleAvailability(reservations, nowDate)

            if (availableNow) {
              // Disponible maintenant → priorité absolue
              if (!bestAvailableNow) {
                bestVehicle = v
                bestAvailableNow = true
                bestNextDate = undefined
                bestBookedUntil = bookedUntil
              } else if (!bookedUntil && bestBookedUntil) {
                // Préférer un véhicule sans réservation imminente
                bestVehicle = v
                bestBookedUntil = undefined
              }
            } else if (!bestAvailableNow) {
              // Pas disponible maintenant → retenir celui dont la prochaine date est la plus proche
              if (!bestNextDate || (nextAvailableDate && new Date(nextAvailableDate) < new Date(bestNextDate))) {
                bestVehicle = v
                bestNextDate = nextAvailableDate
              }
            }
          })

          results.push({
            category,
            vehicle: bestVehicle || data.vehicles[0],
            availableNow: bestAvailableNow,
            nextAvailableDate: bestNextDate,
            bookedUntil: bestBookedUntil,
            vehicleCount: data.vehicles.length,
          })
        })

        // Trier : catégories avec véhicule dispo maintenant en premier
        results.sort((a, b) => {
          if (a.availableNow && !b.availableNow) return -1
          if (!a.availableNow && b.availableNow) return 1
          return 0
        })

        setCategoryData(results)
      })
      .catch((err) => {
        console.error('Erreur chargement vehicules:', err)
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#fbbf24] border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 mt-4 font-medium">Chargement des vehicules...</p>
        </div>
      </section>
    )
  }

  if (categoryData.length === 0) {
    return (
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 bg-[#fbbf24] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
              <Car className="w-4 h-4" />
              Disponibilite
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-black leading-tight mb-4">
              Tous nos vehicules sont
              <span className="block text-[#fbbf24]">actuellement en location</span>
            </h2>
            <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <p className="text-lg">Consultez notre flotte pour reserver a une date ulterieure</p>
            </div>
            <Link
              to="/flotte"
              className="group inline-flex items-center gap-2 bg-black text-[#fbbf24] px-6 py-3 rounded-xl font-bold hover:bg-black/90 transition-all hover:scale-105"
            >
              Voir toute la flotte
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 md:py-28 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#fbbf24]/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#fbbf24]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <span className="inline-flex items-center gap-2 bg-[#fbbf24] text-black px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
              <Car className="w-4 h-4" />
              Disponibles maintenant
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-black leading-tight">
              Nos vehicules
              <span className="block text-[#fbbf24]">prets a partir</span>
            </h2>
          </div>
          <Link
            to="/flotte"
            className="group inline-flex items-center gap-2 bg-black text-[#fbbf24] px-6 py-3 rounded-xl font-bold hover:bg-black/90 transition-all hover:scale-105 self-start md:self-auto"
          >
            Voir toute la flotte
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {categoryData.map(({ category, vehicle, availableNow, nextAvailableDate, bookedUntil, vehicleCount }, index) => {
            const config = CATEGORIES_CONFIG[category] || { icon: '🚗', gradient: 'from-gray-500 to-gray-700' }

            return (
              <Link
                key={category}
                to={`/flotte?categorie=${encodeURIComponent(category)}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 ${
                  availableNow
                    ? 'border-gray-100 hover:border-[#fbbf24]'
                    : 'border-gray-100 hover:border-orange-400'
                }`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                {/* Image du vehicule representatif */}
                <div className="relative h-48 overflow-hidden">
                  {vehicle.imageUrl ? (
                    <img
                      src={vehicle.imageUrl}
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
                      <span className="text-6xl">{config.icon}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                  {/* Badge categorie en haut a gauche */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1.5 bg-[#fbbf24] text-black px-3 py-1.5 rounded-full text-xs font-black shadow-lg">
                      <span>{config.icon}</span>
                      {category}
                    </span>
                  </div>

                  {/* Nombre de vehicules en haut a droite */}
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-black px-2.5 py-1.5 rounded-full text-xs font-bold shadow-md">
                      <Car className="w-3.5 h-3.5" />
                      {vehicleCount} {vehicleCount > 1 ? 'vehicules' : 'vehicule'}
                    </span>
                  </div>

                  {/* Badge disponibilite */}
                  <div className="absolute bottom-14 left-3">
                    {availableNow && bookedUntil ? (
                      <span className="inline-flex items-center gap-1.5 bg-orange-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                        <Clock className="w-3 h-3" />
                        Bientôt réservé
                      </span>
                    ) : availableNow ? (
                      <span className="inline-flex items-center gap-1.5 bg-emerald-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        Disponible
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 bg-orange-500/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                        <Clock className="w-3 h-3" />
                        En location
                      </span>
                    )}
                  </div>

                  {/* Nom du vehicule representatif */}
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-black text-lg drop-shadow-lg truncate">
                      {vehicle.brand} {vehicle.model}
                    </p>
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-5">
                  {/* Disponibilite */}
                  {availableNow && bookedUntil ? (
                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2 rounded-xl mb-4">
                      <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      <p className="text-xs font-bold">
                        Libre aujourd'hui seulement - réservé dès le <span className="font-black">{formatDateFr(bookedUntil)}</span>
                      </p>
                    </div>
                  ) : availableNow ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-xl mb-4">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0" />
                      <p className="text-xs font-bold">Disponible immediatement</p>
                    </div>
                  ) : nextAvailableDate ? (
                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2 rounded-xl mb-4">
                      <Calendar className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      <p className="text-xs font-bold">
                        Disponible des le <span className="font-black">{formatDateFr(nextAvailableDate)}</span>
                      </p>
                    </div>
                  ) : null}

                  {/* Prix + CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      {vehicle.pricePerHalfDay && vehicle.pricePerHalfDay > 0 ? (
                        <div className="flex items-end gap-2.5">
                          <div>
                            <p className="text-xs font-bold text-gray-400 mb-0.5">1/2 J</p>
                            <p className="text-lg font-black text-black leading-none">
                              CHF {vehicle.pricePerHalfDay}
                            </p>
                          </div>
                          <div className="w-px h-8 bg-gray-200" />
                          <div>
                            <p className="text-xs font-bold text-gray-400 mb-0.5">J</p>
                            <p className="text-lg font-black text-black leading-none">
                              CHF {vehicle.pricePerDay}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-2xl font-black text-black">
                            CHF {vehicle.pricePerDay}
                          </p>
                          <p className="text-gray-400 text-xs font-medium">des / jour</p>
                        </>
                      )}
                    </div>
                    <span className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all group-hover:scale-105 ${
                      availableNow
                        ? 'bg-[#fbbf24] text-black group-hover:bg-[#f59e0b]'
                        : 'bg-black text-[#fbbf24] group-hover:bg-black/90'
                    }`}>
                      Voir
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
